import React from 'react';
import { TagInput } from 'components';
import { RESOURCE_PICKER_DEFAULT_REDUCERS } from 'lib/globals';
import useBindToContext from 'hooks/useBindToContext';

export function InitialSelectionIds(resourcesArray, pattern) {
    return resourcesArray.map(({ id }) => ({ id: pattern.replace('{id}', id) }));
}

// Migrated from the deprecated <ResourcePicker> React component (@shopify/app-bridge-react v2)
// to the App Bridge resourcePicker() API exposed on the global `shopify` object by the
// app-bridge.js CDN script (see public/index.html). The legacy component auto-opened on mount
// once the CDN App Bridge took over host messaging; the imperative API has no mounted-modal state,
// so it can only open on an explicit trigger call.
const ShopifyResourcePicker = ({
    value,
    type = 'Product',
    children,
    onChange,
    reducer = false,
    allowMultiple = true,
    BindToContext = null,
    showVariants = false,
    initialSelectionIds,
    showHidden = false,
    verifySelection,
    initialQuery
}) => {
    const [BindedValue, BindedOnChange] = useBindToContext(BindToContext, value, onChange);
    const idFilterRegex = /gid:\/\/shopify\/.*\/([0-9]+)?.*/;
    const formatIds = (value) => {
        if (Array.isArray(value)) {
            return value.map((item) => {
                return formatIds(item);
            });
        } else if (value === Object(value)) {
            let outputObject = {};
            Object.keys(value).forEach((key) => {
                outputObject[key] = formatIds(value[key]);
            });
            return outputObject;
        } else if (typeof value === 'string') {
            const match = value.match(idFilterRegex);
            return match ? match[1] : value;
        }
        return value;
    };

    const applySelection = (selection) => {
        if (verifySelection && !verifySelection(selection)) {
            return;
        }
        let value = formatIds(selection.map(reducer || RESOURCE_PICKER_DEFAULT_REDUCERS[type]));
        if (!allowMultiple) return BindedOnChange(value[0]);
        let mergedItems = BindedValue.concat(value || []);
        let uniqueItems = mergedItems.reduce((acc, obj) => {
            const foundIndex = acc.findIndex((item) => item.id === obj.id);
            if (foundIndex === -1) {
                acc.push(obj);
            }
            return acc;
        }, []);
        BindedOnChange(uniqueItems);
    };

    const openPicker = async () => {
        // The global is registered by the app-bridge.js CDN script + shopify-api-key meta tag.
        if (!window.shopify || typeof window.shopify.resourcePicker !== 'function') {
            console.error('App Bridge resourcePicker API is not available on window.shopify');
            return;
        }

        // showVariants / showHidden only apply to the Product resource type picker.
        const resourceType = type.toLowerCase();
        const options = {
            type: resourceType,
            multiple: allowMultiple
        };
        if (initialQuery) options.query = initialQuery;
        if (initialSelectionIds) options.selectionIds = initialSelectionIds;
        if (resourceType === 'product') {
            options.filter = { variants: !!showVariants, hidden: !!showHidden };
        }

        // Resolves to an array of selected resources, or undefined when the merchant cancels.
        const selection = await window.shopify.resourcePicker(options);
        if (!selection || !selection.length) return;
        applySelection(selection);
    };

    const removeItem = (id) => {
        BindedOnChange(allowMultiple ? BindedValue.filter((item) => item.id != id) : null);
    };

    const tags = allowMultiple
        ? BindedValue
        : BindedValue && !!Object.keys(BindedValue).length
        ? [BindedValue]
        : [];

    return (
        <div>
            {/* pass trigger function to children */}
            {children(openPicker)}
            {!!tags.length && <TagInput tags={tags} close={(id) => removeItem(id)} />}
        </div>
    );
};

export default ShopifyResourcePicker;
